import db from '../data/db';
import Intro from './partials/intro';
import DataTable from './partials/dataTable';
import Pagination from './partials/pagination';
import Search from './partials/search';

const { useState, useEffect } = wp.element;

/**
 * Renders the product inventory with pagination, search, and data table functionalities.
 *
 * This component manages the state for current page, total items, paginated data, and search term.
 * It fetches and displays data based on the provided items per page and country code.
 *
 * @param { Object } props              - The properties passed to the component.
 * @param { number } props.itemsPerPage - Number of items to display per page.
 * @param { string } props.countryCode  - Country code to fetch data for.
 *
 * @return { JSX.Element } The rendered component with intro, search, data table, and pagination.
 */
const ProductInventory = ( { itemsPerPage, countryCode } ) => {
	const [ currentPage, setCurrentPage ] = useState( 1 );
	const [ totalItems, setTotalItems ] = useState( 0 );
	const [ paginatedData, setPaginatedData ] = useState( [] );
	const [ searchTerm, setSearchTerm ] = useState( '' );

	// Return to the first page when searching, this avoids issues when searching on a page great than 1.
	useEffect( ( ) => {
		setCurrentPage( 1 );
	}, [ searchTerm ] );

	useEffect( () => {
		const fetchData = async () => {
			// Fetch all items first (may need optimising this for large datasets)
			const tableName = `${ countryCode }_productInventory`;
			const allItems = await db.table( tableName ).toArray();

			// Filter items based on the search term
			const filteredItems = allItems.filter( item => {
				const name = item.name ? item.name.toLowerCase() : '';
				const articleNumber = item.articleNumber ? item.articleNumber.toLowerCase() : '';
				const term = searchTerm ? searchTerm.toLowerCase() : '';

				return name.includes( term ) || articleNumber.includes( term );
			} );

			// Update total items count based on filtered results
			const total = filteredItems.length;
			setTotalItems( total );

			// Calculate pagination
			const startIndex = ( currentPage - 1 ) * itemsPerPage;
			const paginatedItems = filteredItems.slice( startIndex, startIndex + itemsPerPage );
			setPaginatedData( paginatedItems );
		};

		fetchData();
	}, [ currentPage, searchTerm, itemsPerPage, countryCode ] );

	const handlePageChange = page => {
		setCurrentPage( page );
	};

	return (
		<>
			<Intro countryCode={ countryCode } />
			<Search searchTerm={ searchTerm } setSearchTerm={ setSearchTerm } />
			<DataTable data={ paginatedData } countryCode={ countryCode } />
			<Pagination currentPage={ currentPage } totalPages={ Math.ceil( totalItems / itemsPerPage ) } onPageChange={ handlePageChange } />
		</>
	);
};

export default ProductInventory;
